// src/features/coupon/components/ScratchRewardModal.tsx

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Gift,
  Sparkles,
} from 'lucide-react';


interface ScratchRewardModalProps {
  couponName: string;

  rewardText: string;

  /**
   * 실제 당첨 보상 수량
   *
   * 0이면 꽝으로 표시
   */
  rewardAmount: number | null;

  storeName?: string;

  onClose: () => void;
}


const REVEAL_THRESHOLD =
  0.55;


export default function ScratchRewardModal({
  couponName,
  rewardText,
  rewardAmount,
  storeName,
  onClose,
}: ScratchRewardModalProps) {

  const canvasRef =
    useRef<HTMLCanvasElement | null>(
      null,
    );


  const containerRef =
    useRef<HTMLDivElement | null>(
      null,
    );


  const drawingRef =
    useRef(false);


  const lastPointRef =
    useRef<{
      x: number;
      y: number;
    } | null>(
      null,
    );


  const moveCountRef =
    useRef(0);


  const [
    revealed,
    setRevealed,
  ] =
    useState(false);


  /**
   * =========================================================
   * 꽝 여부
   *
   * rewardAmount === 0
   * -> 꽝
   *
   * null은 아직 값이 없는 것이므로
   * 꽝으로 처리하지 않음
   * =========================================================
   */
  const isNoReward =
    rewardAmount ===
    0;


  /**
   * =========================================================
   * 실제 화면에 표시할 결과
   * =========================================================
   */
  const displayRewardText =
    isNoReward
      ? '꽝'
      : rewardText;


  /**
   * =========================================================
   * Canvas 초기화
   * =========================================================
   */
  const initializeCanvas =
    useCallback(
      (): void => {

        const canvas =
          canvasRef.current;


        const container =
          containerRef.current;


        if (
          !canvas ||
          !container
        ) {
          return;
        }


        const rect =
          container.getBoundingClientRect();


        const ratio =
          window.devicePixelRatio ||
          1;


        canvas.width =
          Math.floor(
            rect.width *
            ratio,
          );


        canvas.height =
          Math.floor(
            rect.height *
            ratio,
          );


        canvas.style.width =
          `${rect.width}px`;


        canvas.style.height =
          `${rect.height}px`;


        const context =
          canvas.getContext(
            '2d',
          );


        if (
          !context
        ) {
          return;
        }


        context.setTransform(
          ratio,
          0,
          0,
          ratio,
          0,
          0,
        );


        /**
         * 복권 은박 느낌
         */
        const gradient =
          context.createLinearGradient(
            0,
            0,
            rect.width,
            rect.height,
          );


        gradient.addColorStop(
          0,
          '#9ca3af',
        );


        gradient.addColorStop(
          0.5,
          '#d1d5db',
        );


        gradient.addColorStop(
          1,
          '#9ca3af',
        );


        context.fillStyle =
          gradient;


        context.fillRect(
          0,
          0,
          rect.width,
          rect.height,
        );


        /**
         * 패턴
         */
        context.globalAlpha =
          0.16;


        context.fillStyle =
          '#ffffff';


        for (
          let x = -100;
          x <
          rect.width + 100;
          x += 26
        ) {

          context.save();


          context.translate(
            x,
            0,
          );


          context.rotate(
            -0.4,
          );


          context.fillRect(
            0,
            0,
            7,
            rect.height * 2,
          );


          context.restore();
        }


        context.globalAlpha =
          1;


        /**
         * 안내 문구
         */
        context.fillStyle =
          '#374151';


        context.font =
          '700 15px sans-serif';


        context.textAlign =
          'center';


        context.textBaseline =
          'middle';


        context.fillText(
          '여기를 긁어주세요',
          rect.width / 2,
          rect.height / 2 - 8,
        );


        context.font =
          '500 11px sans-serif';


        context.fillStyle =
          '#6b7280';


        context.fillText(
          'SCRATCH HERE',
          rect.width / 2,
          rect.height / 2 + 14,
        );

      },
      [],
    );


  /**
   * =========================================================
   * Canvas 초기화 / Resize
   * =========================================================
   */
  useEffect(
    () => {

      initializeCanvas();


      const handleResize =
        (): void => {

          if (
            !revealed
          ) {
            initializeCanvas();
          }
        };


      window.addEventListener(
        'resize',
        handleResize,
      );


      return () => {

        window.removeEventListener(
          'resize',
          handleResize,
        );

      };

    },
    [
      initializeCanvas,
      revealed,
    ],
  );


  /**
   * =========================================================
   * Pointer 위치 계산
   * =========================================================
   */
  const getPoint = (
    event:
      React.PointerEvent<
        HTMLCanvasElement
      >,
  ) => {

    const canvas =
      canvasRef.current;


    if (
      !canvas
    ) {
      return null;
    }


    const rect =
      canvas.getBoundingClientRect();


    return {

      x:
        event.clientX -
        rect.left,


      y:
        event.clientY -
        rect.top,

    };
  };


  /**
   * =========================================================
   * 긁기
   * =========================================================
   */
  const scratch = (
    from: {
      x: number;
      y: number;
    },

    to: {
      x: number;
      y: number;
    },
  ): void => {

    const canvas =
      canvasRef.current;


    if (
      !canvas
    ) {
      return;
    }


    const context =
      canvas.getContext(
        '2d',
      );


    if (
      !context
    ) {
      return;
    }


    context.save();


    context.globalCompositeOperation =
      'destination-out';


    context.lineCap =
      'round';


    context.lineJoin =
      'round';


    context.lineWidth =
      34;


    context.beginPath();


    context.moveTo(
      from.x,
      from.y,
    );


    context.lineTo(
      to.x,
      to.y,
    );


    context.stroke();


    context.restore();
  };


  /**
   * =========================================================
   * 얼마나 긁었는지 검사
   * =========================================================
   */
  const checkScratchProgress =
    (): void => {

      const canvas =
        canvasRef.current;


      if (
        !canvas
      ) {
        return;
      }


      const context =
        canvas.getContext(
          '2d',
        );


      if (
        !context
      ) {
        return;
      }


      const imageData =
        context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        );


      const pixels =
        imageData.data;


      let transparentPixels =
        0;


      /**
       * 모든 픽셀 검사하면 무거우므로
       * 일정 간격으로 샘플링
       */
      const sampleStep =
        40;


      let sampleCount =
        0;


      for (
        let index = 3;
        index <
        pixels.length;
        index +=
        4 * sampleStep
      ) {

        sampleCount +=
          1;


        if (
          pixels[index] <
          40
        ) {
          transparentPixels +=
            1;
        }
      }


      if (
        sampleCount ===
        0
      ) {
        return;
      }


      const scratchedRatio =
        transparentPixels /
        sampleCount;


      if (
        scratchedRatio >=
        REVEAL_THRESHOLD
      ) {
        revealAll();
      }
    };


  /**
   * =========================================================
   * 전체 공개
   * =========================================================
   */
  const revealAll =
    (): void => {

      const canvas =
        canvasRef.current;


      if (
        canvas
      ) {

        const context =
          canvas.getContext(
            '2d',
          );


        context?.clearRect(
          0,
          0,
          canvas.width,
          canvas.height,
        );

      }


      setRevealed(
        true,
      );
    };


  /**
   * =========================================================
   * Pointer Down
   * =========================================================
   */
  const handlePointerDown = (
    event:
      React.PointerEvent<
        HTMLCanvasElement
      >,
  ): void => {

    if (
      revealed
    ) {
      return;
    }


    event.currentTarget
      .setPointerCapture(
        event.pointerId,
      );


    const point =
      getPoint(
        event,
      );


    if (
      !point
    ) {
      return;
    }


    drawingRef.current =
      true;


    lastPointRef.current =
      point;


    scratch(
      point,
      point,
    );
  };


  /**
   * =========================================================
   * Pointer Move
   * =========================================================
   */
  const handlePointerMove = (
    event:
      React.PointerEvent<
        HTMLCanvasElement
      >,
  ): void => {

    if (
      !drawingRef.current ||
      revealed
    ) {
      return;
    }


    const currentPoint =
      getPoint(
        event,
      );


    const previousPoint =
      lastPointRef.current;


    if (
      !currentPoint ||
      !previousPoint
    ) {
      return;
    }


    scratch(
      previousPoint,
      currentPoint,
    );


    lastPointRef.current =
      currentPoint;


    moveCountRef.current +=
      1;


    /**
     * 매 프레임마다 getImageData하면
     * 모바일에서 무거우므로
     * 일정 횟수마다 검사
     */
    if (
      moveCountRef.current %
        12 ===
      0
    ) {
      checkScratchProgress();
    }
  };


  /**
   * =========================================================
   * Pointer End
   * =========================================================
   */
  const handlePointerEnd =
    (): void => {

      drawingRef.current =
        false;


      lastPointRef.current =
        null;


      checkScratchProgress();
    };


  return (
    <div
      className="
        fixed
        inset-0
        z-[100]
        flex
        items-center
        justify-center
        bg-black/60
        px-4
      "
    >

      <div
        className="
          w-full
          max-w-sm
          rounded-3xl
          bg-white
          p-5
          shadow-2xl
        "
        role="dialog"
        aria-modal="true"
      >

        {/* =====================================================
            상단
            ===================================================== */}
        <div
          className="
            text-center
          "
        >

          <div
            className="
              mx-auto
              flex
              h-11
              w-11
              items-center
              justify-center
              rounded-full
              bg-main/10
              text-main
            "
          >
            <Sparkles
              size={22}
            />
          </div>


          <p
            className="
              mt-3
              text-xs
              font-bold
              tracking-[0.16em]
              text-main
            "
          >
            LUCKY COUPON
          </p>


          <h2
            className="
              mt-1
              text-xl
              font-bold
              text-gray-900
            "
          >
            {couponName}
          </h2>


          <p
            className="
              mt-1
              text-xs
              text-gray-500
            "
          >
            복권을 긁어서 결과를 확인하세요.
          </p>

        </div>


        {/* =====================================================
            스크래치 영역
            ===================================================== */}
        <div
          ref={
            containerRef
          }
          className="
            relative
            mt-5
            h-40
            overflow-hidden
            rounded-2xl
            border
            border-gray-200
            bg-gradient-to-br
            from-amber-50
            via-white
            to-yellow-50
          "
        >

          {/* 실제 결과 */}
          <div
            className="
              absolute
              inset-0
              flex
              flex-col
              items-center
              justify-center
              px-4
              text-center
            "
          >

            <div
              className="
                flex
                h-10
                w-10
                items-center
                justify-center
                rounded-full
                bg-main/10
                text-main
              "
            >
              <Gift
                size={20}
              />
            </div>


            <p
              className="
                mt-2
                text-[11px]
                font-semibold
                text-gray-400
              "
            >
              {isNoReward
                ? '결과'
                : '당첨 보상'}
            </p>


            <strong
              className={`
                mt-1
                font-black

                ${
                  isNoReward
                    ? `
                      text-3xl
                      text-gray-700
                    `
                    : `
                      text-xl
                      text-gray-900
                    `
                }
              `}
            >
              {displayRewardText}
            </strong>


            {!isNoReward &&
              storeName && (
                <p
                  className="
                    mt-1
                    text-xs
                    text-gray-500
                  "
                >
                  {storeName}
                </p>
              )}

          </div>


          {/* 긁는 은박 Canvas */}
          {!revealed && (
            <canvas
              ref={
                canvasRef
              }
              onPointerDown={
                handlePointerDown
              }
              onPointerMove={
                handlePointerMove
              }
              onPointerUp={
                handlePointerEnd
              }
              onPointerCancel={
                handlePointerEnd
              }
              onPointerLeave={
                handlePointerEnd
              }
              className="
                absolute
                inset-0
                z-10
                h-full
                w-full
                cursor-crosshair
                touch-none
              "
            />
          )}

        </div>


        {/* =====================================================
            안내 / 버튼
            ===================================================== */}
        {!revealed ? (

          <div
            className="
              mt-4
              text-center
            "
          >

            <p
              className="
                text-xs
                text-gray-400
              "
            >
              손가락이나 마우스로 은박 부분을 긁어주세요.
            </p>


            <button
              type="button"
              onClick={
                revealAll
              }
              className="
                mt-3
                text-xs
                font-semibold
                text-gray-400
                underline
                underline-offset-4
                transition
                hover:text-gray-700
              "
            >
              결과 바로 보기
            </button>

          </div>

        ) : (

          <div
            className="
              mt-4
              text-center
            "
          >

            {isNoReward ? (

              <p
                className="
                  text-sm
                  font-bold
                  text-gray-600
                "
              >
                아쉽네요. 다음 기회를 노려보세요!
              </p>

            ) : (

              <p
                className="
                  text-sm
                  font-bold
                  text-gray-900
                "
              >
                🎉 축하합니다!
              </p>

            )}


            <button
              type="button"
              onClick={
                onClose
              }
              className="
                mt-4
                h-10
                w-full
                rounded-xl
                bg-main
                px-4
                text-sm
                font-bold
                text-white
                transition
                hover:opacity-90
              "
            >
              {isNoReward
                ? '확인'
                : '보상 받기 완료'}
            </button>

          </div>

        )}

      </div>

    </div>
  );
}